import type { ReactNode, SyntheticEvent } from "react";
import classNames from "classnames";
import { preventBaseUIHandler } from "./usePickerLevelKeyboard";
import styles from "./PickerItem.module.scss";

export interface PickerItemActionProps {
    children: ReactNode;
    className?: string;
}

// Native key and focus events that bubbled (through the React tree, portals
// included) out of an item action. Base UI's list and popup handlers only
// stand aside for a `preventBaseUIHandler()` made in their own merged
// chain, so the list and popup read this mark rather than the row's flag.
const itemActionEvents = new WeakSet<Event>();

const markItemActionEvent = (event: SyntheticEvent) => {
    itemActionEvents.add(event.nativeEvent);
};

/** Whether a key or focus event bubbled out of a `Picker.ItemAction`. */
export const isItemActionEvent = (event: SyntheticEvent) =>
    itemActionEvents.has(event.nativeEvent);

/**
 * Whether an event a `Picker.Item` receives started in one of its nested
 * controls: inside a `Picker.ItemAction` of the row, or outside the row's DOM
 * altogether (a portal its content renders, such as a nested menu's popup,
 * whose events still bubble through the React tree). Works in the capture
 * phase too.
 */
export const isRowNestedControlEvent = (event: SyntheticEvent<HTMLElement>) => {
    const { target, currentTarget } = event;
    if (!(target instanceof Node)) {
        return false;
    }
    if (!currentTarget.contains(target)) {
        return true;
    }
    const action =
        target instanceof Element
            ? target.closest("[data-picker-item-action]")
            : null;
    return action !== null && currentTarget.contains(action);
};

/** A row handler that lets a nested control's event skip Base UI's own row handler. */
export const skipBaseUIForNestedControl = (
    event: SyntheticEvent<HTMLElement>,
) => {
    if (isRowNestedControlEvent(event)) {
        preventBaseUIHandler(event);
    }
};

/**
 * Wraps an interactive control inside a `Picker.Item` (a "..." menu, a
 * button, a link) in `leading`, `trailing` or the label. Presses, keys and
 * focus that start inside it, including in an overlay it portals, reach the
 * control and never select or toggle the row, close the popup, move the list
 * highlight, or get pulled back to the search input. The rest of the row
 * still selects.
 */
export const PickerItemAction = ({
    children,
    className,
}: PickerItemActionProps) => (
    <span
        className={classNames(styles.itemAction, className)}
        data-picker-item-action=""
        onKeyDown={markItemActionEvent}
        onFocus={markItemActionEvent}
    >
        {children}
    </span>
);
